import { useEffect, useMemo, useState } from "react";
import {
    ComposedChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
    ResponsiveContainer,
    CartesianGrid,
    ReferenceLine,
} from "recharts";
import { TrendingUp, AlertCircle, CalendarClock, Layers, Car as CarIcon } from "lucide-react";
import { api, fmtMXN } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

const CHART_TICK_STYLE = { fontSize: 11, fill: "#64748B" };
const CHART_LEGEND_STYLE = { fontSize: 12 };
const COMPARE_COLORS = ["#0F172A", "#0EA5E9", "#F59E0B", "#10B981", "#E11D48", "#7C3AED", "#EA580C", "#64748B"];

const MONTH_NAMES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
const formatMonthLabel = (ym) => {
    const [y, m] = ym.split("-");
    return `${MONTH_NAMES[Number(m) - 1]} ${y}`;
};

const MILESTONE_ICON_COLOR = {
    insurance_renewal: "#7C3AED",
    verification: "#0EA5E9",
    major_service: "#EA580C",
};

function yTickFormatter(v) {
    if (Math.abs(v) < 1000) return `$${v.toFixed(0)}`;
    const k = v / 1000;
    return `$${Number.isInteger(k) ? k.toFixed(0) : k.toFixed(1)}k`;
}

const MONTH_NAMES_SHORT = MONTH_NAMES;
function formatMilestoneBrief(m, includePlate) {
    const d = new Date(`${m.date}T00:00:00`);
    const dateStr = `${d.getDate()} ${MONTH_NAMES_SHORT[d.getMonth()]}`;
    const platePart = includePlate && m.plate ? `${m.plate} — ` : "";
    const costPart = m.cost ? ` (${fmtMXN(m.cost)})` : "";
    return `${platePart}${m.label}${costPart} — ${dateStr}`;
}

function ProjectionTooltip({ active, payload, label }) {
    if (!active || !payload || !payload.length) return null;
    const d = payload[0]?.payload;
    if (!d) return null;
    const income = d.income_actual ?? d.income_projected;
    const expense = d.expense_actual ?? d.expense_projected;
    return (
        <div className="bg-white rounded-lg border border-slate-200 px-3 py-2 shadow-sm max-w-xs">
            <p className="text-xs font-semibold text-slate-700 mb-1">
                {formatMonthLabel(label)} {d.projected && <span className="text-slate-400 font-normal">(proyectado)</span>}
            </p>
            <p className="text-xs text-emerald-600">Ingresos: {fmtMXN(income)}</p>
            <p className="text-xs text-rose-600">Gastos: {fmtMXN(expense)}</p>
            <p className="text-xs text-slate-700">Neto: {fmtMXN((income || 0) - (expense || 0))}</p>
            {d.milestoneDetail && d.milestoneDetail.length > 0 && (
                <div className="mt-1 pt-1 border-t border-slate-100 space-y-0.5">
                    {d.milestoneDetail.map((line, i) => (
                        <p key={i} className="text-[11px] text-slate-500 leading-snug">{line}</p>
                    ))}
                </div>
            )}
        </div>
    );
}

function CompareTooltip({ active, payload, label }) {
    if (!active || !payload || !payload.length) return null;
    const rows = payload.filter((p) => p.value !== null && p.value !== undefined);
    if (!rows.length) return null;
    return (
        <div className="bg-white rounded-lg border border-slate-200 px-3 py-2 shadow-sm">
            <p className="text-xs font-semibold text-slate-700 mb-1">{formatMonthLabel(label)}</p>
            {rows.map((r) => (
                <p key={r.dataKey} className="text-xs" style={{ color: r.color }}>
                    {r.name}: {fmtMXN(r.value)}
                </p>
            ))}
        </div>
    );
}

/** Shared chart + summary cards + table for a single projection dataset (one car or the whole fleet). */
function ProjectionChartAndTable({ data, loading, error, compactMilestones = false, chartTitle = "Histórico y proyección", testidPrefix = "projection", horizonMonths = 6 }) {
    const milestonesByMonth = useMemo(() => {
        const map = {};
        (data?.milestones || []).forEach((m) => {
            if (!map[m.month]) map[m.month] = [];
            map[m.month].push(m);
        });
        return map;
    }, [data]);

    const chartData = useMemo(() => {
        if (!data) return [];
        const history = data.monthly_history.map((h) => ({ ...h, income_actual: h.income, expense_actual: h.expense, income_projected: null, expense_projected: null }));
        const projection = data.projection.map((p) => ({ ...p, income_actual: null, expense_actual: null, income_projected: p.income, expense_projected: p.expense }));
        const merged = [...history, ...projection];
        if (history.length && projection.length) {
            merged[history.length - 1].income_projected = merged[history.length - 1].income_actual;
            merged[history.length - 1].expense_projected = merged[history.length - 1].expense_actual;
        }
        return merged.map((row) => {
            const ms = milestonesByMonth[row.month] || [];
            const milestoneLabel = compactMilestones
                ? (ms.length ? `${ms.length} hito${ms.length > 1 ? "s" : ""} · ${fmtMXN(ms.reduce((s, m) => s + (m.cost || 0), 0))}` : "")
                : ms.map((m) => `${m.label}${m.cost ? ` (${fmtMXN(m.cost)})` : ""}`).join(" · ");
            const milestoneDetail = ms.map((m) => formatMilestoneBrief(m, compactMilestones));
            return { ...row, milestoneLabel, milestoneDetail };
        });
    }, [data, milestonesByMonth, compactMilestones]);

    const projectionTotals = useMemo(() => {
        if (!data) return null;
        const income = data.projection.reduce((s, p) => s + p.income, 0);
        const expense = data.projection.reduce((s, p) => s + p.expense, 0);
        const milestoneCost = data.projection.length
            ? (data.milestones || []).filter((m) => m.month >= data.projection[0].month).reduce((s, m) => s + (m.cost || 0), 0)
            : 0;
        return { income, expense, net: income - expense, milestoneCost };
    }, [data]);

    const staleNote = useMemo(() => {
        if (!data || !data.monthly_history.length) return null;
        const lastMonth = data.monthly_history[data.monthly_history.length - 1].month;
        const now = new Date();
        const nowYM = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
        const [ly, lm] = lastMonth.split("-").map(Number);
        const [ny, nm] = nowYM.split("-").map(Number);
        const diff = (ny * 12 + nm) - (ly * 12 + lm);
        return diff >= 2 ? lastMonth : null;
    }, [data]);

    return (
        <div className="space-y-6">
            {error && (
                <Card className="border-rose-200 bg-rose-50 shadow-sm" data-testid={`${testidPrefix}-error-banner`}>
                    <CardContent className="py-3 flex items-center gap-2 text-sm text-rose-700 font-medium">
                        <AlertCircle size={16} /> No se pudo cargar la proyección. Intenta de nuevo.
                    </CardContent>
                </Card>
            )}

            {staleNote && (
                <Card className="border-sky-200 bg-sky-50 shadow-sm" data-testid={`${testidPrefix}-stale-data-note`}>
                    <CardContent className="py-3 flex items-center gap-2 text-sm text-sky-700 font-medium">
                        <AlertCircle size={16} />
                        El último movimiento registrado es de {formatMonthLabel(staleNote)}. La proyección parte de ese mes, por lo que podría no reflejar los próximos meses reales si está inactivo.
                    </CardContent>
                </Card>
            )}

            {data && data.insufficient_data && (
                <Card className="border-amber-200 bg-amber-50 shadow-sm" data-testid={`${testidPrefix}-insufficient-data-banner`}>
                    <CardContent className="py-3 flex items-center gap-2 text-sm text-amber-700 font-medium">
                        <AlertCircle size={16} />
                        Hay menos de 2 meses con movimientos registrados — la proyección no será estadísticamente confiable.
                    </CardContent>
                </Card>
            )}

            {projectionTotals && (
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                    <Card className="border-slate-200 shadow-sm">
                        <CardContent className="p-5">
                            <p className="text-xs text-slate-500">Ingresos proyectados ({horizonMonths} m)</p>
                            <p className="text-xl font-semibold text-emerald-600 mt-1" data-testid={`${testidPrefix}-total-income`}>{fmtMXN(projectionTotals.income)}</p>
                        </CardContent>
                    </Card>
                    <Card className="border-slate-200 shadow-sm">
                        <CardContent className="p-5">
                            <p className="text-xs text-slate-500">Gastos proyectados ({horizonMonths} m)</p>
                            <p className="text-xl font-semibold text-rose-600 mt-1" data-testid={`${testidPrefix}-total-expense`}>{fmtMXN(projectionTotals.expense)}</p>
                        </CardContent>
                    </Card>
                    <Card className="border-slate-200 shadow-sm">
                        <CardContent className="p-5">
                            <p className="text-xs text-slate-500">Neto proyectado ({horizonMonths} m)</p>
                            <p className="text-xl font-semibold text-slate-900 mt-1" data-testid={`${testidPrefix}-total-net`}>{fmtMXN(projectionTotals.net)}</p>
                        </CardContent>
                    </Card>
                    <Card className="border-slate-200 shadow-sm">
                        <CardContent className="p-5">
                            <p className="text-xs text-slate-500">Costo de hitos próximos</p>
                            <p className="text-xl font-semibold text-violet-600 mt-1" data-testid={`${testidPrefix}-total-milestone-cost`}>{fmtMXN(projectionTotals.milestoneCost)}</p>
                        </CardContent>
                    </Card>
                </div>
            )}

            <Card className="border-slate-200 shadow-sm">
                <CardHeader>
                    <CardTitle className="text-lg font-display font-medium flex items-center gap-2">
                        <TrendingUp size={18} className="text-slate-500" /> {chartTitle}
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {!loading && (chartData.length === 0 || error) ? (
                        <div className="h-72 grid place-items-center text-sm text-slate-400">
                            {error ? "No se pudo cargar la gráfica." : "Sin datos disponibles"}
                        </div>
                    ) : (
                        <div className="h-80" data-testid={`${testidPrefix}-chart`}>
                            <ResponsiveContainer width="100%" height="100%">
                                <ComposedChart data={chartData} margin={{ top: 28, right: 20, bottom: 0, left: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                                    <XAxis dataKey="month" tick={CHART_TICK_STYLE} tickFormatter={formatMonthLabel} />
                                    <YAxis tick={CHART_TICK_STYLE} tickFormatter={yTickFormatter} />
                                    <Tooltip content={<ProjectionTooltip />} />
                                    <Legend wrapperStyle={CHART_LEGEND_STYLE} />
                                    <Line type="monotone" dataKey="income_actual" name="Ingresos" stroke="#10B981" strokeWidth={2} dot={{ r: 3 }} connectNulls={false} />
                                    <Line type="monotone" dataKey="income_projected" name="Ingresos (proyectado)" stroke="#10B981" strokeWidth={2} strokeDasharray="5 4" dot={{ r: 3 }} connectNulls={false} />
                                    <Line type="monotone" dataKey="expense_actual" name="Gastos" stroke="#E11D48" strokeWidth={2} dot={{ r: 3 }} connectNulls={false} />
                                    <Line type="monotone" dataKey="expense_projected" name="Gastos (proyectado)" stroke="#E11D48" strokeWidth={2} strokeDasharray="5 4" dot={{ r: 3 }} connectNulls={false} />
                                    {Object.keys(milestonesByMonth).map((month) => (
                                        <ReferenceLine
                                            key={month}
                                            x={month}
                                            stroke={compactMilestones ? "#64748B" : (MILESTONE_ICON_COLOR[milestonesByMonth[month][0].type] || "#64748B")}
                                            strokeDasharray="2 2"
                                            label={{
                                                value: compactMilestones
                                                    ? `${milestonesByMonth[month].length} hito${milestonesByMonth[month].length > 1 ? "s" : ""}`
                                                    : milestonesByMonth[month].map((m) => m.label).join(" + "),
                                                position: "top",
                                                fill: compactMilestones ? "#64748B" : (MILESTONE_ICON_COLOR[milestonesByMonth[month][0].type] || "#64748B"),
                                                fontSize: 9,
                                            }}
                                        />
                                    ))}
                                </ComposedChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </CardContent>
            </Card>

            <Card className="border-slate-200 shadow-sm">
                <CardHeader>
                    <CardTitle className="text-lg font-display font-medium flex items-center gap-2">
                        <CalendarClock size={18} className="text-slate-500" /> Detalle mensual
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-xs uppercase bg-slate-50 text-slate-500 tracking-wider">
                                    <th className="text-left px-4 py-3 font-semibold">Mes</th>
                                    <th className="text-right px-4 py-3 font-semibold">Ingresos</th>
                                    <th className="text-right px-4 py-3 font-semibold">Gastos</th>
                                    <th className="text-right px-4 py-3 font-semibold">Neto</th>
                                    <th className="text-left px-4 py-3 font-semibold">Hitos</th>
                                </tr>
                            </thead>
                            <tbody>
                                {chartData.map((row) => (
                                    <tr key={row.month} className={`border-t border-slate-100 ${row.projected ? "bg-slate-50/60" : ""}`} data-testid={`${testidPrefix}-row-${row.month}`}>
                                        <td className="px-4 py-3 font-medium text-slate-900">
                                            {formatMonthLabel(row.month)}
                                            {row.projected && <Badge variant="secondary" className="ml-2 text-[10px]">Proyectado</Badge>}
                                        </td>
                                        <td className="px-4 py-3 text-right text-emerald-600">{fmtMXN(row.income_actual ?? row.income_projected)}</td>
                                        <td className="px-4 py-3 text-right text-rose-600">{fmtMXN(row.expense_actual ?? row.expense_projected)}</td>
                                        <td className="px-4 py-3 text-right font-medium text-slate-900">{fmtMXN((row.income_actual ?? row.income_projected ?? 0) - (row.expense_actual ?? row.expense_projected ?? 0))}</td>
                                        <td className="px-4 py-3 text-xs text-slate-500" title={row.milestoneDetail?.length ? row.milestoneDetail.join("\n") : undefined}>{row.milestoneLabel || "—"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}

function ByCarTab({ cars, carId, setCarId, data, loading, error, horizon }) {
    return (
        <div className="space-y-6">
            <div className="max-w-xs">
                <Select value={carId} onValueChange={setCarId}>
                    <SelectTrigger data-testid="projection-car-select"><SelectValue placeholder="Selecciona un auto" /></SelectTrigger>
                    <SelectContent>
                        {cars.map((c) => <SelectItem key={c.id} value={c.id}>{c.plate} · {c.brand} {c.model}</SelectItem>)}
                    </SelectContent>
                </Select>
            </div>
            {cars.length === 0 ? (
                <Card className="border-slate-200 shadow-sm"><CardContent className="py-10 text-center text-slate-400">Aún no hay autos registrados.</CardContent></Card>
            ) : (
                <ProjectionChartAndTable data={data} loading={loading} error={error} testidPrefix="projection" horizonMonths={horizon} />
            )}
        </div>
    );
}

function CompareTab({ cars, selected, toggleCar, compareData, loading, error, horizon }) {
    const compareChartData = useMemo(() => {
        if (!compareData.length) return [];
        const monthSet = new Set();
        compareData.forEach((c) => {
            c.monthly_history.forEach((h) => monthSet.add(h.month));
            c.projection.forEach((p) => monthSet.add(p.month));
        });
        const months = Array.from(monthSet).sort();
        const rows = months.map((month) => {
            const row = { month };
            compareData.forEach((c) => {
                const h = c.monthly_history.find((x) => x.month === month);
                const p = c.projection.find((x) => x.month === month);
                row[`net_actual_${c.car_id}`] = h ? h.net : null;
                row[`net_projected_${c.car_id}`] = p ? p.net : null;
            });
            return row;
        });
        compareData.forEach((c) => {
            if (!c.monthly_history.length || !c.projection.length) return;
            const lastMonth = c.monthly_history[c.monthly_history.length - 1].month;
            const row = rows.find((r) => r.month === lastMonth);
            if (row) row[`net_projected_${c.car_id}`] = row[`net_actual_${c.car_id}`];
        });
        return rows;
    }, [compareData]);

    return (
        <div className="space-y-6">
            <div>
                <p className="text-xs font-medium text-slate-600 mb-2">Autos a comparar ({selected.length} seleccionados)</p>
                <div className="max-h-44 overflow-y-auto rounded-md border border-slate-200 divide-y divide-slate-100 max-w-md" data-testid="compare-car-list">
                    {cars.map((c) => (
                        <label key={c.id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50">
                            <Checkbox checked={selected.includes(c.id)} onCheckedChange={() => toggleCar(c.id)} data-testid={`compare-car-checkbox-${c.id}`} />
                            <span className="text-slate-700">{c.plate} · {c.brand} {c.model}</span>
                        </label>
                    ))}
                </div>
            </div>

            {selected.length === 0 ? (
                <Card className="border-slate-200 shadow-sm"><CardContent className="py-10 text-center text-slate-400">Selecciona al menos un auto para comparar.</CardContent></Card>
            ) : (
                <>
                    {error && (
                        <Card className="border-rose-200 bg-rose-50 shadow-sm" data-testid="compare-error-banner">
                            <CardContent className="py-3 flex items-center gap-2 text-sm text-rose-700 font-medium">
                                <AlertCircle size={16} /> No se pudo cargar la comparación. Intenta de nuevo.
                            </CardContent>
                        </Card>
                    )}
                    <Card className="border-slate-200 shadow-sm">
                        <CardHeader>
                            <CardTitle className="text-lg font-display font-medium flex items-center gap-2">
                                <Layers size={18} className="text-slate-500" /> Comparación de Neto por auto
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {!loading && (compareChartData.length === 0 || error) ? (
                                <div className="h-72 grid place-items-center text-sm text-slate-400">
                                    {error ? "No se pudo cargar la gráfica." : "Sin datos para los autos seleccionados"}
                                </div>
                            ) : (
                                <div className="h-80" data-testid="compare-chart">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <ComposedChart data={compareChartData} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                                            <XAxis dataKey="month" tick={CHART_TICK_STYLE} tickFormatter={formatMonthLabel} />
                                            <YAxis tick={CHART_TICK_STYLE} tickFormatter={yTickFormatter} />
                                            <Tooltip content={<CompareTooltip />} />
                                            <Legend wrapperStyle={CHART_LEGEND_STYLE} />
                                            {compareData.flatMap((c, idx) => {
                                                const color = COMPARE_COLORS[idx % COMPARE_COLORS.length];
                                                return [
                                                    <Line key={`${c.car_id}-a`} type="monotone" dataKey={`net_actual_${c.car_id}`} name={`${c.plate} (real)`} stroke={color} strokeWidth={2} dot={{ r: 3 }} connectNulls={false} />,
                                                    <Line key={`${c.car_id}-p`} type="monotone" dataKey={`net_projected_${c.car_id}`} name={`${c.plate} (proyectado)`} stroke={color} strokeWidth={2} strokeDasharray="5 4" dot={{ r: 3 }} connectNulls={false} />,
                                                ];
                                            })}
                                        </ComposedChart>
                                    </ResponsiveContainer>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    <Card className="border-slate-200 shadow-sm">
                        <CardHeader>
                            <CardTitle className="text-lg font-display font-medium flex items-center gap-2">
                                <CarIcon size={18} className="text-slate-500" /> Totales proyectados por auto ({horizon} m)
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="p-0">
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="text-xs uppercase bg-slate-50 text-slate-500 tracking-wider">
                                            <th className="text-left px-4 py-3 font-semibold">Auto</th>
                                            <th className="text-right px-4 py-3 font-semibold">Ingresos proy.</th>
                                            <th className="text-right px-4 py-3 font-semibold">Gastos proy.</th>
                                            <th className="text-right px-4 py-3 font-semibold">Neto proy.</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {compareData.map((c) => {
                                            const income = c.projection.reduce((s, p) => s + p.income, 0);
                                            const expense = c.projection.reduce((s, p) => s + p.expense, 0);
                                            return (
                                                <tr key={c.car_id} className="border-t border-slate-100" data-testid={`compare-totals-row-${c.car_id}`}>
                                                    <td className="px-4 py-3 font-medium text-slate-900">{c.plate} · {c.brand} {c.model}</td>
                                                    <td className="px-4 py-3 text-right text-emerald-600">{fmtMXN(income)}</td>
                                                    <td className="px-4 py-3 text-right text-rose-600">{fmtMXN(expense)}</td>
                                                    <td className="px-4 py-3 text-right font-medium text-slate-900">{fmtMXN(income - expense)}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </CardContent>
                    </Card>
                </>
            )}
        </div>
    );
}

function FleetTab({ data, loading, error, horizon }) {
    return (
        <div className="space-y-4">
            <p className="text-sm text-slate-500" data-testid="fleet-car-count">
                Suma el historial y proyección de los <strong>{data?.car_count ?? 0}</strong> autos registrados en la flotilla.
            </p>
            <ProjectionChartAndTable
                data={data}
                loading={loading}
                error={error}
                compactMilestones
                chartTitle="Histórico y proyección — Flotilla completa"
                testidPrefix="projection-fleet"
                horizonMonths={horizon}
            />
        </div>
    );
}

export default function Projection() {
    const [cars, setCars] = useState([]);
    const [horizon, setHorizon] = useState("6");

    // Por auto
    const [carId, setCarId] = useState("");
    const [carData, setCarData] = useState(null);
    const [carLoading, setCarLoading] = useState(false);
    const [carError, setCarError] = useState(false);

    // Comparar autos
    const [compareSelected, setCompareSelected] = useState([]);
    const [compareData, setCompareData] = useState([]);
    const [compareLoading, setCompareLoading] = useState(false);
    const [compareError, setCompareError] = useState(false);
    const toggleCompareCar = (id) => setCompareSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

    // Flotilla completa
    const [fleetData, setFleetData] = useState(null);
    const [fleetLoading, setFleetLoading] = useState(true);
    const [fleetError, setFleetError] = useState(false);

    useEffect(() => {
        api.get("/cars").then((r) => {
            setCars(r.data);
            if (r.data.length > 0) setCarId(r.data[0].id);
        });
    }, []);

    useEffect(() => {
        let ignore = false;
        setFleetLoading(true);
        setFleetError(false);
        api.get(`/analytics/projection-fleet?horizon_months=${horizon}`)
            .then((r) => { if (!ignore) setFleetData(r.data); })
            .catch(() => { if (!ignore) setFleetError(true); })
            .finally(() => { if (!ignore) setFleetLoading(false); });
        return () => { ignore = true; };
    }, [horizon]);

    useEffect(() => {
        if (!carId) return;
        let ignore = false;
        setCarLoading(true);
        setCarData(null);
        setCarError(false);
        api.get(`/analytics/projection/${carId}?horizon_months=${horizon}`)
            .then((r) => { if (!ignore) setCarData(r.data); })
            .catch(() => { if (!ignore) setCarError(true); })
            .finally(() => { if (!ignore) setCarLoading(false); });
        return () => { ignore = true; };
    }, [carId, horizon]);

    useEffect(() => {
        if (compareSelected.length === 0) {
            setCompareData([]);
            setCompareError(false);
            return;
        }
        let ignore = false;
        setCompareLoading(true);
        setCompareError(false);
        api.get(`/analytics/projection-compare?car_ids=${compareSelected.join(",")}&horizon_months=${horizon}`)
            .then((r) => { if (!ignore) setCompareData(r.data); })
            .catch(() => { if (!ignore) { setCompareData([]); setCompareError(true); } })
            .finally(() => { if (!ignore) setCompareLoading(false); });
        return () => { ignore = true; };
    }, [compareSelected, horizon]);

    return (
        <div className="space-y-6" data-testid="projection-page">
            <header className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <p className="text-xs font-semibold tracking-[0.05em] uppercase text-slate-500">Analítica</p>
                    <h1 className="text-3xl font-semibold tracking-tight text-slate-900 mt-1 font-display">Proyección</h1>
                    <p className="text-sm text-slate-500 mt-1">
                        Regresión lineal sobre el historial completo de ingresos y gastos, proyectada {horizon} meses hacia adelante, con hitos de seguros, verificaciones y servicios mayores.
                    </p>
                </div>
                <div className="w-44">
                    <label className="text-xs font-medium text-slate-500 mb-1 block">Horizonte de proyección</label>
                    <Select value={horizon} onValueChange={setHorizon}>
                        <SelectTrigger data-testid="projection-horizon-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="3">3 meses</SelectItem>
                            <SelectItem value="6">6 meses</SelectItem>
                            <SelectItem value="12">12 meses</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </header>

            <Tabs defaultValue="by-car">
                <TabsList>
                    <TabsTrigger value="by-car" data-testid="projection-tab-by-car">Por auto</TabsTrigger>
                    <TabsTrigger value="compare" data-testid="projection-tab-compare">Comparar autos</TabsTrigger>
                    <TabsTrigger value="fleet" data-testid="projection-tab-fleet">Flotilla completa</TabsTrigger>
                </TabsList>
                <TabsContent value="by-car" className="pt-4">
                    <ByCarTab cars={cars} carId={carId} setCarId={setCarId} data={carData} loading={carLoading} error={carError} horizon={horizon} />
                </TabsContent>
                <TabsContent value="compare" className="pt-4">
                    <CompareTab cars={cars} selected={compareSelected} toggleCar={toggleCompareCar} compareData={compareData} loading={compareLoading} error={compareError} horizon={horizon} />
                </TabsContent>
                <TabsContent value="fleet" className="pt-4">
                    <FleetTab data={fleetData} loading={fleetLoading} error={fleetError} horizon={horizon} />
                </TabsContent>
            </Tabs>
        </div>
    );
}
